/**
 * RenewalAlertsBoard — Iter-156 · Phase 6a Section 30.
 * Iter-160 · Phase 2 — adds "Mark Renewed" action per contract so
 * Sales/Admin can explicitly record a renewal signature. This is the
 * only source of truth for renewal_rate_pct on the Executive tile.
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WINDOWS = [180, 120, 90, 60, 30];
const TONES = { 180: '#3b82f6', 120: '#3b82f6', 90: '#facc15', 60: '#f97316', 30: '#ef4444' };

export default function RenewalAlertsBoard() {
  const [alerts, setAlerts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/iter156/renewals/alerts`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setAlerts(d.windows || {}); }
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const markRenewed = async (contractId) => {
    setBusyId(contractId); setNote('');
    try {
      const r = await fetch(`${API}/api/contracts/${contractId}/mark-renewed`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          renewal_signed_at: new Date().toISOString(),
          renewed_by: 'sales_command_center',
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      setNote(`Renewal recorded for ${contractId}. Executive renewal-rate tile will refresh on next fetch.`);
      await load();
    } catch (e) {
      setNote(`Could not mark renewed: ${String(e.message).slice(0, 120)}`);
    }
    setBusyId(null);
  };

  return (
    <div className="space-y-4" data-testid="renewal-alerts-board">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {WINDOWS.map((w) => (
          <div key={w} className="rounded-lg p-3 border text-center" style={{ backgroundColor: '#141414', borderColor: TONES[w] + '66' }} data-testid={`renewal-window-${w}`}>
            <p className="text-[10px] uppercase tracking-widest" style={{ color: TONES[w] }}>{w}-day window</p>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{(alerts[String(w)] || []).length}</p>
            <p className="text-[10px] text-zinc-500 mt-1">contracts</p>
          </div>
        ))}
      </div>
      {note && <p className="text-xs" style={{ color: '#7CFC8A' }} data-testid="renewal-note">{note}</p>}
      <div className="rounded-lg border border-zinc-700 overflow-x-auto">
        <table className="w-full text-sm" data-testid="renewal-list-table">
          <thead className="bg-[#141414] text-zinc-400 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Window</th>
              <th className="text-left p-3">Contract</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-center p-3">Days to Renewal</th>
              <th className="text-center p-3">Term</th>
              <th className="text-center p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {WINDOWS.flatMap((w) => (alerts[String(w)] || []).map((r) => (
              <tr key={`${w}-${r.contract_id}`} className="border-t border-zinc-800" data-testid={`renewal-row-${w}-${r.contract_id}`}>
                <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: TONES[w], color: w <= 60 ? '#fff' : '#111' }}>{w}-day</span></td>
                <td className="p-3 text-white">{r.contract_id}</td>
                <td className="p-3 text-zinc-300">{r.customer || '—'}</td>
                <td className="p-3 text-center text-white">{r.days_to_renewal}</td>
                <td className="p-3 text-center text-zinc-400">{r.term_months || '—'}m</td>
                <td className="p-3 text-center">
                  <Button
                    size="sm"
                    onClick={() => markRenewed(r.contract_id)}
                    disabled={busyId === r.contract_id}
                    className="text-[10px] font-bold uppercase tracking-widest"
                    style={{ backgroundColor: '#10b981', color: '#fff' }}
                    data-testid={`renewal-mark-renewed-${r.contract_id}`}
                  >
                    {busyId === r.contract_id
                      ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Saving</>
                      : <><CheckCircle2 className="h-3 w-3 mr-1" />Mark Renewed</>}
                  </Button>
                </td>
              </tr>
            )))}
            {WINDOWS.every((w) => (alerts[String(w)] || []).length === 0) && (
              <tr><td colSpan="6" className="p-6 text-center text-zinc-500" data-testid="renewal-empty">No contracts inside any renewal window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
